import axios from 'axios';
import { getToken } from './tokenStore';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth APIs
export const sendOTP = (mobile) => api.post('/auth/send-otp', { mobile });
export const verifyOTP = (mobile, otp) => api.post('/auth/verify-otp', { mobile, otp });
export const createPassword = (mobile, password, name) => 
  api.post('/auth/create-password', { mobile, password, name });
export const login = (mobile, password) => api.post('/auth/login', { mobile, password });
export const adminLogin = (username, password) => 
  api.post('/auth/admin-login', { username, password });
export const resetPassword = (mobile, otp, newPassword) => 
  api.post('/auth/reset-password', { mobile, otp, new_password: newPassword });

// User APIs
export const getProfile = () => api.get('/user/profile');
export const updateProfile = (data) => api.put('/user/profile', data);
export const switchRole = () => api.post('/user/switch-role');
export const activateBoth = () => api.post('/user/activate-both');
export const uploadProfilePic = (blob) => {
  const fd = new FormData();
  fd.append('file', blob, 'avatar.jpg');
  return api.post('/user/upload-profile-pic', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uploadIdProof = (file) => {
  const fd = new FormData();
  fd.append('file', file, file.name || 'id-proof');
  return api.post('/user/upload-id-proof', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Admin invoices
export const getAdminInvoices = (page = 1, limit = 100, search = '') =>
  api.get(`/admin/invoices?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
export const getAdminUserInvoices = (userId) =>
  api.get(`/admin/users/${userId}/invoices`);
export const getAdminInvoicePdfUrl = (orderId) => {
  return { url: `${API_BASE}/admin/invoices/${orderId}/pdf`, token: getToken() };
};

// Membership APIs
export const getMembershipPlans = () => api.get('/membership/plans');
export const purchaseMembership = (planId) => api.post('/membership/purchase', { plan_id: planId });

// Services APIs
export const getServices = () => api.get('/services');
export const payForService = (serviceId) => api.post('/services/pay', { service_id: serviceId });

// Search APIs
export const searchProviders = (filters) => api.post('/search/providers', filters);

// Booking APIs
export const createBooking = (data) => api.post('/bookings/create', data);
export const prepayBooking = (data) => api.post('/bookings/prepay', data);
export const assignProvider = (bookingId, data) => api.post(`/bookings/${bookingId}/assign-provider`, data);
export const getHirerBookings = () => api.get('/bookings/hirer');
export const getProviderBookings = () => api.get('/bookings/provider');
export const respondToBooking = (bookingId, status, reason = null) => 
  api.post(`/bookings/${bookingId}/respond`, { booking_id: bookingId, status, reason });
export const completeBooking = (bookingId) => api.post(`/bookings/${bookingId}/complete`);

// Admin APIs
export const getAdminStats = () => api.get('/admin/stats');
export const getAllUsers = (page = 1, limit = 100, filter = 'all', search = '') => api.get(`/admin/users?page=${page}&limit=${limit}&filter=${filter}&search=${encodeURIComponent(search)}`);
export const getAllBookings = (page = 1, limit = 100) => api.get(`/admin/bookings?page=${page}&limit=${limit}`);
export const getAllTransactions = (page = 1, limit = 100) => api.get(`/admin/transactions?page=${page}&limit=${limit}`);
export const payProvider = (bookingId) => api.post(`/admin/pay-provider/${bookingId}`);
export const getSOSAlerts = () => api.get('/admin/sos-alerts');
export const getAllWithdrawals = () => api.get('/admin/withdrawals');
export const processWithdrawal = (withdrawalId, status) => 
  api.post(`/admin/withdrawals/${withdrawalId}/process?status=${status}`);

// SOS APIs
export const sendSOSAlert = (data) => api.post('/sos/alert', data);

// Withdrawal APIs
export const requestWithdrawal = (data) => api.post('/withdrawals/request', data);
export const getWithdrawalHistory = () => api.get('/withdrawals/history');

// Review APIs
export const createReview = (data) => api.post('/reviews/create', data);
export const getProviderReviews = (providerId) => api.get(`/reviews/provider/${providerId}`);
export const getMyReviews = () => api.get('/reviews/my');
export const getProviderProfile = (providerId) => api.get(`/providers/${providerId}`);

// Notification APIs
export const getNotifications = () => api.get('/notifications');
export const markNotificationRead = (notificationId) => api.post(`/notifications/${notificationId}/read`);
export const markAllNotificationsRead = () => api.post('/notifications/read-all');
export const getUnreadCount = () => api.get('/notifications/unread-count');

// Invoice endpoints
export const getInvoices = () => api.get('/invoices');
export const getInvoice = (invoiceNumber) => api.get(`/invoices/${invoiceNumber}`);

// Admin enhanced endpoints
export const searchUserByMobile = (mobile) => api.get(`/admin/users/search?mobile=${mobile}`);
export const approveUser = (userId) => api.post(`/admin/users/${userId}/approve`);
export const deactivateUser = (userId) => api.post(`/admin/users/${userId}/deactivate`);
export const bulkApproveUsers = (userIds) => api.post('/admin/users/bulk-approve', { user_ids: userIds });
export const getMonthlyReport = () => api.get('/admin/monthly-report');

// Admin extended user management
export const getUserDetails = (userId) => api.get(`/admin/users/${userId}/details`);
export const grantMembership = (userId, planId) => api.post(`/admin/users/${userId}/grant-membership`, { plan_id: planId });
export const bulkGrantMembership = (userIds, planId) => api.post('/admin/users/bulk-grant-membership', { user_ids: userIds, plan_id: planId });
export const blockUser = (userId) => api.post(`/admin/users/${userId}/block`);
export const unblockUser = (userId) => api.post(`/admin/users/${userId}/unblock`);
export const deleteUser = (userId) => api.delete(`/admin/users/${userId}`);
export const markUserPaid = (userId) => api.post(`/admin/users/${userId}/mark-paid`);
export const updateBookingSettlement = (bookingId, status) => api.post(`/admin/bookings/${bookingId}/settlement`, { status });
export const downloadReport = (type, startDate, endDate) => {
  let url = `/admin/reports/download?report_type=${type}`;
  if (startDate) url += `&start_date=${startDate}`;
  if (endDate) url += `&end_date=${endDate}`;
  return api.get(url);
};

// Contact form
export const submitContactForm = (data) => api.post('/contact', data);

// Payment APIs (QR + Screenshot)
export const createPaymentOrder = (data) => api.post('/payment/create-order', data);
export const getPaymentStatus = (orderId) => api.get(`/payment/status/${orderId}`);
export const uploadMembershipScreenshot = (planId, file) => {
  const formData = new FormData();
  formData.append('plan_id', planId);
  formData.append('screenshot', file);
  return api.post('/payment/screenshot/membership/v2', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const uploadBookingScreenshot = (serviceId, hours, date, time, file) => {
  const formData = new FormData();
  formData.append('service_id', serviceId);
  formData.append('hours', hours);
  formData.append('date', date);
  formData.append('time', time);
  formData.append('screenshot', file);
  return api.post('/payment/screenshot/booking', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getUserPaymentStatus = () => api.get('/user/payment-status');
// Admin payment verification
export const getPendingPayments = () => api.get('/admin/pending-payments');
export const getPaymentScreenshot = (paymentId) => api.get(`/admin/payment/${paymentId}/screenshot`);
export const approvePayment = (paymentId) => api.post(`/admin/payment/${paymentId}/approve`);
export const rejectPayment = (paymentId) => api.post(`/admin/payment/${paymentId}/reject`);

export default api;
